import React, { useMemo } from 'react';
import { Modal, Tabs, Badge } from '../../../design-system';
import { useAdminStore } from '../../../store/adminStore';
import { OnboardingApplication } from '../../../services/adminApi';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { ApproveDialog } from './ApproveDialog';
import { RejectDialog } from './RejectDialog';
import styles from './ApplicationDetailModal.module.css';

interface ApplicationDetailModalProps {
  application: OnboardingApplication | null;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: (reason: string, notes: string) => void;
}

export const ApplicationDetailModal: React.FC<ApplicationDetailModalProps> = ({
  application,
  onClose,
  onApprove,
  onReject,
}) => {
  const {
    detailModalOpen,
    approveDialogOpen,
    rejectDialogOpen,
    openApproveDialog,
    openRejectDialog,
  } = useAdminStore();

  const tabs = useMemo(() => {
    if (!application) return [];

    return [
      {
        id: 'info',
        label: 'Information',
        content: <InformationTab application={application} />,
      },
      {
        id: 'documents',
        label: 'Documents',
        content: <DocumentsTab application={application} />,
      },
      {
        id: 'audit',
        label: 'Audit Log',
        content: <AuditLogTab application={application} />,
      },
    ];
  }, [application]);

  if (!application) return null;

  return (
    <>
      <Modal
        isOpen={detailModalOpen}
        onClose={onClose}
        size="lg"
        closeOnBackdropClick={!approveDialogOpen && !rejectDialogOpen}
        closeOnEsc={!approveDialogOpen && !rejectDialogOpen}
      >
        <Modal.Header>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              <h2 className={styles.title}>{application.store_name}</h2>
              <Badge status={application.status as any}>
                {application.status === 'PENDING'
                  ? 'Pending Review'
                  : application.status === 'APPROVED'
                  ? 'Approved'
                  : (application.status === 'REJECTED' || application.status === 'DENIED')
                  ? 'Rejected'
                  : application.status}
              </Badge>
            </div>
            <div className={styles.headerMeta}>
              <p className={styles.email}>{application.email}</p>
              <p className={styles.date}>
                Submitted: {formatDate(application.created_at)}
              </p>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Tabs tabs={tabs} defaultTabId="info" />
        </Modal.Body>

        <Modal.Footer>
          {application.status === 'PENDING' && (
            <div className={styles.footerActions}>
              <button
                className={`${styles.button} ${styles.approveButton}`}
                onClick={openApproveDialog}
                type="button"
              >
                ✓ Approve
              </button>
              <button
                className={`${styles.button} ${styles.rejectButton}`}
                onClick={openRejectDialog}
                type="button"
              >
                ✕ Reject
              </button>
            </div>
          )}
          <button
            className={`${styles.button} ${styles.closeButton}`}
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Nested dialogs */}
      {onApprove && <ApproveDialog onApprove={onApprove} />}
      {onReject && <RejectDialog onReject={onReject} />}
    </>
  );
};

// Information Tab
interface InformationTabProps {
  application: OnboardingApplication;
}

const InformationTab: React.FC<InformationTabProps> = ({ application }) => {
  return (
    <div className={styles.infoGrid}>
      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Business Information</h3>
        <div className={styles.infoRow}>
          <span className={styles.label}>Business Name:</span>
          <span className={styles.value}>{application.store_name}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.label}>LLC Name:</span>
          <span className={styles.value}>{application.llc_name}</span>
        </div>
      </div>

      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Contact Information</h3>
        <div className={styles.infoRow}>
          <span className={styles.label}>Full Name:</span>
          <span className={styles.value}>{application.full_name}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.label}>Email:</span>
          <span className={styles.value}>
            <a href={`mailto:${application.email}`}>{application.email}</a>
          </span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.label}>Phone:</span>
          <span className={styles.value}>{formatPhoneNumber(application.contact_phone)}</span>
        </div>
      </div>

      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Address</h3>
        <div className={styles.infoRow}>
          <span className={styles.label}>Street:</span>
          <span className={styles.value}>{application.address_line1}</span>
        </div>
        {application.address_line2 && (
          <div className={styles.infoRow}>
            <span className={styles.label}></span>
            <span className={styles.value}>{application.address_line2}</span>
          </div>
        )}
        <div className={styles.infoRow}>
          <span className={styles.label}>City, State, ZIP:</span>
          <span className={styles.value}>
            {application.city}, {application.state} {application.zip_code}
          </span>
        </div>
      </div>

      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Banking Information</h3>
        <div className={styles.infoRow}>
          <span className={styles.label}>Bank Name:</span>
          <span className={styles.value}>{application.bank_name}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.label}>Account Type:</span>
          <span className={styles.value}>
            {application.account_type === 'checking' ? 'Checking' : 'Savings'}
          </span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.label}>Routing Number:</span>
          <span className={styles.value}>{maskRoutingNumber(application.routing_number)}</span>
        </div>
      </div>

      <div className={styles.infoSection}>
        <h3 className={styles.sectionTitle}>Agreement</h3>
        <div className={styles.infoRow}>
          <span className={styles.label}>Terms Accepted:</span>
          <span className={styles.value}>
            {formatDateTime(application.terms_accepted_at)}
          </span>
        </div>
        {application.signature_data_url && (
          <div className={styles.signaturePreview}>
            <p className={styles.label}>Signature:</p>
            <img
              src={application.signature_data_url}
              alt="Customer Signature"
              className={styles.signatureImage}
            />
          </div>
        )}
      </div>

      {(application.status === 'REJECTED' || application.status === 'DENIED') && application.denial_reason && (
        <div className={`${styles.infoSection} ${styles.rejectionSection}`}>
          <h3 className={styles.sectionTitle}>Rejection Reason</h3>
          <div className={styles.infoRow}>
            <span className={styles.value}>{application.denial_reason}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Documents Tab
interface DocumentsTabProps {
  application: OnboardingApplication;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ application }) => {
  const documents = [
    {
      name: 'Tax ID Document',
      base64: application.tax_id_document_base64,
      filename: application.tax_id_document_filename,
    },
    {
      name: 'Federal ID Document',
      base64: application.federal_id_document_base64,
      filename: application.federal_id_document_filename,
    },
    {
      name: 'Tobacco License Document',
      base64: application.tobacco_license_document_base64,
      filename: application.tobacco_license_document_filename,
    },
  ].filter((doc) => doc.base64);

  const openBase64Document = (base64: string, filename?: string) => {
    const link = document.createElement('a');
    link.href = base64;
    link.target = '_blank';
    if (filename) link.download = filename;
    link.click();
  };

  return (
    <div className={styles.documentsContainer}>
      {documents.length === 0 ? (
        <p className={styles.emptyMessage}>No documents uploaded</p>
      ) : (
        <div className={styles.documentsList}>
          {documents.map((doc) => (
            <div key={doc.name} className={styles.documentItem}>
              <div className={styles.documentIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
                </svg>
              </div>
              <div className={styles.documentInfo}>
                <p className={styles.documentName}>{doc.name}</p>
                <p className={styles.documentPath}>{doc.filename || 'Document'}</p>
              </div>
              <button
                className={styles.previewButton}
                onClick={() => openBase64Document(doc.base64!, doc.filename)}
                type="button"
              >
                Preview
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Audit Log Tab
interface AuditLogTabProps {
  application: OnboardingApplication;
}

const AuditLogTab: React.FC<AuditLogTabProps> = ({ application }) => {
  const auditEvents = useMemo(() => {
    const events = [
      {
        action: 'SUBMITTED',
        timestamp: application.created_at,
        user: 'Customer',
        description: 'Application submitted',
      },
    ];

    if (application.reviewed_at && application.reviewed_by_user_id) {
      events.push({
        action: application.status === 'APPROVED' ? 'APPROVED' : (application.status === 'DENIED' ? 'DENIED' : 'REJECTED'),
        timestamp: application.reviewed_at,
        user: `Admin (ID: ${application.reviewed_by_user_id})`,
        description:
          application.status === 'APPROVED'
            ? 'Application approved'
            : `Application rejected: ${application.denial_reason || 'No reason provided'}`,

      });
    }

    return events;
  }, [application]);

  return (
    <div className={styles.auditLog}>
      <div className={styles.timeline}>
        {auditEvents.map((event, index) => (
          <div key={index} className={styles.timelineEvent}>
            <div className={`${styles.timelineDot} ${styles[event.action.toLowerCase()]}`} />
            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <p className={styles.timelineAction}>{event.action}</p>
                <span className={styles.timelineUser}>{event.user}</span>
              </div>
              <p className={styles.timelineDescription}>{event.description}</p>
              <p className={styles.timelineTime}>{formatDateTime(event.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper functions
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length !== 10) return phone;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

const maskRoutingNumber = (routing: string): string => {
  if (routing.length < 4) return routing;
  return `****${routing.slice(-4)}`;
};
